import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { FolderOpen, MoreVertical, ChevronDown, Check } from 'lucide-react';
import { StatusBadge } from 'src/components/common/StatusBadge';
import DataTable from 'src/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { useOrganizations } from 'src/services/api/queries/organizationQueries';
import { DescriptionCell } from './DescriptionCell';

const NameCell = ({ value }) => (
  <span className="inline-flex items-center gap-2.5">
    <span className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
      <FolderOpen className="w-[15px] h-[15px]" strokeWidth={1.9} />
    </span>
    <span className="text-[13px] font-semibold text-foreground-strong group-hover:text-primary transition-colors">
      {value}
    </span>
  </span>
);

const ActionsCell = ({ row, onEdit, onPublish, onDeactivate }) => (
  <div
    role="presentation"
    className="flex items-center justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
  >
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {row.status === 'DRAFT' && (
          <DropdownMenuItem onClick={() => onPublish(row.id)}>
            Publish
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => onEdit(row.id)}
          disabled={row.status === 'ACTIVE'}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDeactivate(row.id)}>
          Deactivate
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

export const TestCollectionsTable = ({
  collections,
  onEdit,
  onDeactivate,
  onPublish,
  onRowClick,
  currentPage = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  loading = false,
}) => {
  const [publishDialog, setPublishDialog] = useState({ open: false, id: null });
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [deactivateDialog, setDeactivateDialog] = useState({
    open: false,
    id: null,
  });

  const orgDropdownRef = useRef(null);

  const { data: orgData } = useOrganizations({ pageNo: 1, pageSize: 100 });
  const orgOptions = (orgData?.data?.items ?? [])
    .filter((o) => o.is_active)
    .map((o) => ({ value: o.org_id, label: o.name }));

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(e.target)
      ) {
        setOrgDropdownOpen(false);
      }
    };
    if (orgDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [orgDropdownOpen]);

  const handlePublishClick = (id) => {
    setSelectedOrgId(null);
    setPublishDialog({ open: true, id });
  };

  const handlePublishConfirm = () => {
    if (publishDialog.id) onPublish(publishDialog.id, selectedOrgId);
    setPublishDialog({ open: false, id: null });
    setSelectedOrgId(null);
  };

  const handleDeactivateClick = (id) => setDeactivateDialog({ open: true, id });

  const handleDeactivateConfirm = () => {
    if (deactivateDialog.id) onDeactivate(deactivateDialog.id);
    setDeactivateDialog({ open: false, id: null });
  };

  const columns = [
    {
      key: 'id',
      header: 'ID',
      width: '100px',
      minWidth: '80px',
      render: (v) => (
        <span className="font-mono text-[12px] text-muted-foreground tracking-wide">
          {v}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      minWidth: '220px',
      render: (v) => <NameCell value={v} />,
    },
    {
      key: 'description',
      header: 'Description',
      minWidth: '120px',
      render: (v, row) => (
        <DescriptionCell
          value={v}
          label={row.name}
          maxWidthClass="max-w-[44ch]"
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      minWidth: '110px',
      render: (v) => <StatusBadge value={v} />,
    },
    {
      key: 'actions',
      header: '',
      width: '56px',
      minWidth: '56px',
      render: (_v, row) => (
        <ActionsCell
          row={row}
          onEdit={onEdit}
          onPublish={handlePublishClick}
          onDeactivate={handleDeactivateClick}
        />
      ),
    },
  ];

  return (
    <>
      <DataTable
        data={collections}
        columns={columns}
        loading={loading}
        tableLayout="auto"
        hover
        stickyHeader
        pagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={Math.max(
          1,
          Math.ceil((totalItems || 0) / (pageSize || 10))
        )}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onRowClick={onRowClick}
      />

      <AlertDialog
        open={publishDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setPublishDialog({ open: false, id: null });
            setSelectedOrgId(null);
            setOrgDropdownOpen(false);
          }
        }}
      >
        <AlertDialogContent className="overflow-visible">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Publish</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to publish this test collection? Once
              published, it will be available for use.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-1">
            <p className="text-[12.5px] font-[560] text-foreground mb-1.5">
              Organization <span className="text-destructive">*</span>
            </p>
            <div className="relative" ref={orgDropdownRef}>
              <button
                type="button"
                onClick={() => setOrgDropdownOpen((v) => !v)}
                className={`w-full h-[38px] px-3 pr-9 rounded-lg border text-[13px] bg-background flex items-center justify-between text-left outline-none transition-[border-color,box-shadow] ${
                  orgDropdownOpen
                    ? 'border-primary ring-[3px] ring-primary/10'
                    : 'border-input hover:border-border'
                }`}
              >
                <span
                  className={
                    selectedOrgId
                      ? 'text-foreground-strong'
                      : 'text-muted-foreground'
                  }
                >
                  {orgOptions.find((o) => o.value === selectedOrgId)?.label ||
                    'Select an organization…'}
                </span>
                <ChevronDown
                  className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-transform ${
                    orgDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {orgDropdownOpen && (
                <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-background border border-border rounded-lg shadow-lg z-[9999] max-h-[180px] overflow-y-auto py-1">
                  {orgOptions.map((opt) => (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={selectedOrgId === opt.value}
                      tabIndex={0}
                      onClick={() => {
                        setSelectedOrgId(opt.value);
                        setOrgDropdownOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedOrgId(opt.value);
                          setOrgDropdownOpen(false);
                        }
                      }}
                      className={`flex items-center justify-between px-3 py-2 text-[13px] cursor-pointer transition-colors ${
                        selectedOrgId === opt.value
                          ? 'bg-brand-bg-subtle text-primary font-medium'
                          : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      {opt.label}
                      {selectedOrgId === opt.value && (
                        <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPublishDialog({ open: false, id: null });
                setSelectedOrgId(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublishConfirm}
              disabled={!selectedOrgId}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deactivateDialog.open}
        onOpenChange={(open) =>
          !open && setDeactivateDialog({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deactivate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate this test collection? It will
              no longer be available for use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeactivateDialog({ open: false, id: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivateConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
